import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { MediumCustomIcon } from "../../layer00";

type StyledCardProps = {
  title: string;
  icon?: ReactNode;
  badge?: ReactNode;
  action?: ReactNode;
  description?: string;
  hasHeaderDivider?: boolean;
  isCollapsible?: boolean;
  isInitiallyOpen?: boolean;
  children: ReactNode;
};

export const StyledCard = (props: StyledCardProps) => {
  const theme = useTheme();
  const isCollapsible = props.isCollapsible ?? true;

  const [isOpen, setIsOpen] = useState(props.isInitiallyOpen ?? true);

  const isCollapsed = isCollapsible && !isOpen;

  return (
    <Card
      sx={{
        paddingLeft: {
          xs: "1rem",
          sm: "2rem",
        },
        paddingRight: {
          xs: "1rem",
          sm: "2rem",
        },
      }}
    >
      <CardHeader
        disableTypography
        onClick={isCollapsible ? () => setIsOpen((prev) => !prev) : undefined}
        sx={{
          cursor: isCollapsible ? "pointer" : undefined,
          borderBottom:
            props.hasHeaderDivider && !isCollapsed
              ? "1px solid #F5F7FC"
              : "none",
          "& .MuiCardHeader-action": {
            alignSelf: "center",
            marginTop: 0,
            marginRight: 0,
          },
        }}
        title={
          <Stack direction="row" columnGap=".5rem" alignItems="center">
            {props.icon}
            <Typography variant="cardTitle" minWidth={0}>
              {props.title}
            </Typography>
            {props.badge}
          </Stack>
        }
        action={
          props.action || isCollapsible ? (
            <Stack direction="row" columnGap=".5rem" alignItems="center">
              {props.action && (
                <Box onClick={(e) => e.stopPropagation()}>{props.action}</Box>
              )}
              {isCollapsible && (
                <IconButton
                  sx={{
                    border: "none",
                  }}
                >
                  <MediumCustomIcon
                    icon={isOpen ? "CHEVRONUP" : "CHEVRONDOWN"}
                    stroke={theme.palette.secondary.light}
                  />
                </IconButton>
              )}
            </Stack>
          ) : undefined
        }
      />
      <Collapse in={!isCollapsed}>
        <CardContent
          sx={{
            background: "none",
            paddingTop: props.hasHeaderDivider ? "1rem" : 0,
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: "2rem",
          }}
        >
          <Stack direction="column" rowGap=".5rem">
            {props.description && (
              <Typography variant="cardBodyRegular" color="#53565A">
                {props.description}
              </Typography>
            )}
            <Box>{props.children}</Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};
